<template>
  <div>
    <wc-page-title :loading='isLoading' :title='title' :subtitle='subtitle'>
      <template v-slot:rightSide v-if='project && project.analytics && project.analytics.conflicts'>

        <v-tooltip top open-delay='500' v-if='!project.archived'>
          <template v-slot:activator='{ on }'>
            <v-icon
              v-if='!project.archived'
              v-on='on'
              :color='conflicts.color'
              class='mr-2'
              @click='showConflictsDialog = true'
            >
              {{ conflicts.icon }}
            </v-icon>
          </template>
          <span class='caption'>{{ conflicts.tooltip }}</span>
        </v-tooltip>

        <wc-burger-menu>
          <v-list-item @click='toggleHold' :disabled='project.archived'>
            <v-list-item-icon>
              <v-icon :color='project.on_hold ? "success" : "error"'>{{$icons.hold.icon}}</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ project.on_hold ? "Remove Project Hold" : "Put Project on hold"}}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>

           <v-list-item @click='toggleArchive'>
            <v-list-item-icon>
              <v-icon :color='project.archived ? "success" : "error"'>{{ project.archived ? "mdi-archive-arrow-up" : "mdi-archive" }}</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ project.archived ? "Un-archive Project" : "Archive Project"}}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </wc-burger-menu>

      </template>
    </wc-page-title>

    <v-container v-if='project && project.on_hold' class='mb-n6'>
      <v-alert border='bottom' type='error' :icon='$icons.hold.icon'>This Project has been placed on hold</v-alert>
    </v-container>
    <v-container v-if='project && project.archived' class='mb-n6'>
      <v-alert border='bottom' type='error'>
        This Project is archived. <br>
        <span class='font-italic caption'>
          Don't forget to zero out any invoices and archive them (as well as archiving any open work orders)
        </span>
      </v-alert>
    </v-container>

    <v-container v-if='precheckRequired' class='mb-n6'>
      <v-alert border='bottom' icon='mdi-alert' color='purple' colored-border class='purple lighten-5'>
        <b>Pre-Check Required</b>
        <span class='subtitle-2 ml-2'>Please confirm all project details and pricing.</span>
        <br>
        <span class='font-italic caption'>
          Be sure to double check that the Notes to Customer are written appropriately.
        </span>
      </v-alert>
    </v-container>

    <wc-dialog
      v-if='project'
      :showDialog='showConflictsDialog'
      :title='"Conflicts for - " + project.name'
      hideCancel
      @save='showConflictsDialog = false'
      saveText='Close'
    >
      <template v-if='project && project.analytics && project.analytics.conflicts && project.analytics.conflicts.length'>
        <template v-for='(conflict, index) in project.analytics.conflicts'>
          <div :class='conflict.can_ignore === false ? "error--text font-weight-bold" : "warning--text"' :key='index'>
            {{conflict.value + " - " + conflict.text}}
          </div>
        </template>
      </template>
      <v-card-text v-else class='success--text'>No conflicts found</v-card-text>
    </wc-dialog>

    <router-view
      v-if='project'
      :project='project'
      @update='update'
    ></router-view>
  </div>
</template>

<script>
import projectsAPI from '@/modules/projects/api/projects';
import trackerAPI from '@/api/tracker';
import snowAPI from '@/modules/snow/api';
import { mapState } from 'vuex';
import moment from 'moment';
import _ from 'lodash';

export default {
  name: 'snow-project-base',
  data: () => ({
    isLoading: true,
    showConflictsDialog: false,
    project: null
  }),
  computed: {
    title () {
      if (this.project) {
        return this.project.name
      }
      return ''
    },
    subtitle () {
      if (this.project) {
        return this.project.site_address
      }
      return ''
    },
    conflicts() {
      if (_.some(this.project.analytics.conflicts, p => p.can_ignore === false)) {
        return { icon: "mdi-alert-circle" , color: "error", tooltip: "Click to view conflicts (at least one fatal)" }
      } else if (this.project.analytics.conflicts.length) {
        return { icon: "mdi-alert-circle-outline" , color: "warning", tooltip: "Click to view conflicts" }
      } else {
        return { icon: "mdi-check-circle-outline" , color: "success", tooltip: "This project has no conflicts" }
      }
    },
    precheckRequired () {
      if (this.project && !this.project.archived && this.project.staging_location_obj && this.project.project_detail.renewal_status == "P" && ("staging" in this.project.analytics) && !this.project.analytics.staging.declined && this.project.staging_location_obj.order != 4) {
        return true
      }
      return false
    },
    menu () {
      return [
        {
          title: this.overview.title,
          icon: this.overview.icon,
          route: { name: 'SnowOverview', params: { id: this.$route.params.id } }
        },
        {
          title: this.workorders.title,
          icon: this.workorders.icon,
          route: { name: 'ProjectWOs', params: { id: this.$route.params.id } }
        },
        {
          title: this.crewVisits.title,
          icon: this.crewVisits.icon,
          route: { name: 'SnowCrewVisits', params: { id: this.$route.params.id } }
        },
        {
          title: this.invoices.title,
          icon: this.invoices.icon,
          route: { name: 'ProjectInvoices', params: { id: this.$route.params.id } }
        },
        {
          title: 'Estimates',
          icon: this.estimates.icon,
          route: { name: 'SnowEstimates', params: { id: this.$route.params.id } }
        },
        {
          title: 'Timesheet Details',
          icon: this.$store.state.icons.modules.timesheets.icon,
          route: { name: 'SnowTimesheetDetails' }
        },
        {
          title: this.actionTracker.title,
          icon: this.actionTracker.icon,
          route: { name: 'SnowActionTracker', params: { id: this.$route.params.id } }
        }
      ]
    },
    ...mapState({
      overview: state => state.icons.routes.overview,
      actionTracker: state => state.icons.routes.actionTracker,
      workorders: state => state.icons.modules.workorders,
      invoices: state => state.icons.dataBox.invoices,
      estimates: state => state.icons.modules.estimates,
      crewVisits: state => state.icons.dataBox.crewVisits,
    })
  },
  mounted () {
    this.getProject()
  },
  methods: {
    async getProject () {
      this.isLoading
      this.project = await projectsAPI.getProject(this.$route.params.id)
      this.$store.dispatch('loadSubnavTitle', 'Project Menu')
      this.$store.dispatch('loadSubnav', this.menu)
      this.isLoading = false
    },
    async update (proj) {
      this.isLoading = true
      console.log(proj)
      if (proj.analytics.staging) {
        if (proj.project_detail.renewal_status == "O" && this.project.project_detail.renewal_status == "P") {
          this.createNewLog("Pre-check complete - Renewal Status set to OFFER", proj)
        } else if (proj.project_detail.renewal_status == "D" && this.project.project_detail.renewal_status == "P") {
          if (proj.confirmDecline == 0) {
            proj.analytics.staging.declined = true
            this.createNewLog("Pre-check complete - Renewal Status set to DECLINED - Project moved to Cancelled/Declined", proj)
          } else if (proj.confirmDecline == 1) {
            // this option removed from SnowDetailsForm - MJ
            this.createNewLog("Pre-check complete - Renewal Status set to DECLINED - however NOT cancelled for this season", proj)
          }
        }
      }
      await snowAPI.updateSnowProject(proj.project_detail)
      this.project = await projectsAPI.updateProject(proj)
      this.isLoading = false
    },
    createNewLog (action, proj) {
      if (proj.analytics.staging) {
        let newLog = {}
        newLog.taken_by = this.$store.state.auth.profile.name_display
        newLog.action_taken = action 
        newLog.date = moment().format('YYYY-MM-DD').toString()
        newLog.time = moment().format('HH:mm:ss').toString()
        proj.analytics.staging.log.push(newLog)
      } else {
        console.log("Error - Action not recorded: " + action)
        console.log(proj)
      }
    },
    async recordAction (action) {
      const tracker = {
          'tracker_data': {
            'description': action,
            'taken_by': this.$store.state.auth.profile.id,
            'action_date': moment().format('YYYY-MM-DD').toString(),
          },
          'project': this.project.id
      }
      trackerAPI.createProjectTracker(tracker);
      if (this.$route.name === 'SnowActionTracker') {
        this.$router.go()
      }
    },
    toggleArchive () {
      this.project.archived = !this.project.archived;
      const description = this.project.archived ? "Project Archived" : "Project has been Un-archived";
      this.recordAction(description);
      this.update(this.project);
    },
    toggleHold () {
      this.project.on_hold = !this.project.on_hold;
      const description = this.project.on_hold ? "Project placed on hold" : "Hold removed from Project";
      this.recordAction(description);
      this.update(this.project);
    }
  }
}
</script>
